import { Wifi, MapPin, Layers, ArrowUpRight } from "lucide-react";

const services = [
  {
    icon: Wifi,
    tag: "Anywhere",
    title: "Remote Pitching",
    desc: "Video-driven mechanics breakdown, custom velocity programs, weekly check-ins, and on-demand feedback. Train from your facility, your field, your home.",
    bullets: [
      "Customized throwing program",
      "Baseball-specific strength plan",
      "Video mechanical breakdowns",
      "Weekly video call",
    ],
  },
  {
    icon: MapPin,
    tag: "Hands-on",
    title: "In-Person Sessions",
    desc: "Live, individualized coaching where you actually feel the changes. Drill work, mound reps, and real-time cueing built around your delivery.",
    bullets: [
      "1-on-1 training",
      "Live mechanical adjustments",
      "Personalized velocity training",
    ],
    featured: true,
  },
  {
    icon: Layers,
    tag: "Best of both",
    title: "Hybrid Development",
    desc: "Monthly in-person check-ins paired with remote programming and weekly accountability. The fastest path for serious athletes.",
    bullets: [
      "Weekly live sessions",
      "Full week-to-week remote work",
      "Access all developmental resources",
    ],
  },
];

export default function Services() {
  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 px-6 md:px-10 bg-[#0a0a0a] border-t border-[#1a1a1a]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-px bg-[#eab308]" />
            <span className="font-condensed uppercase tracking-[0.25em] text-xs text-[#eab308]">
              Programs
            </span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
            Three Ways To <span className="text-[#eab308]">Train.</span>
            <br />
            One Standard. Elite.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                data-testid={`service-card-${i}`}
                className={`relative group p-8 md:p-10 border transition-all hover:-translate-y-1 ${
                  s.featured
                    ? "bg-[#eab308] text-black border-[#eab308]"
                    : "bg-[#141414] border-[#2a2a2a] hover:border-[#eab308]"
                }`}
              >
                <div className="flex items-center justify-between mb-10">
                  <div
                    className={`w-12 h-12 flex items-center justify-center border ${
                      s.featured
                        ? "bg-black text-[#eab308] border-black"
                        : "bg-[#0a0a0a] border-[#2a2a2a] text-[#eab308]"
                    }`}
                  >
                    <Icon size={20} strokeWidth={2} />
                  </div>
                  <span
                    className={`font-condensed uppercase text-[10px] tracking-[0.25em] ${
                      s.featured ? "text-black/70" : "text-neutral-500"
                    }`}
                  >
                    {s.tag}
                  </span>
                </div>
                <h3 className="font-display text-3xl md:text-4xl mb-4 leading-none">
                  {s.title}
                </h3>
                <p
                  className={`text-sm md:text-base mb-8 ${
                    s.featured ? "text-black/80" : "text-neutral-400"
                  }`}
                >
                  {s.desc}
                </p>
                <ul className="space-y-2 mb-10">
                  {s.bullets.map((b, j) => (
                    <li
                      key={j}
                      className={`flex items-start gap-3 text-sm font-condensed uppercase tracking-wider ${
                        s.featured ? "text-black" : "text-neutral-200"
                      }`}
                    >
                      <span
                        className={`mt-2 inline-block w-1.5 h-1.5 ${
                          s.featured ? "bg-black" : "bg-[#eab308]"
                        }`}
                      />
                      {b}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`service-cta-${i}`}
                  onClick={() => scrollTo("apply")}
                  className={`inline-flex items-center gap-2 font-condensed uppercase tracking-[0.2em] text-sm font-bold border-b pb-1 transition-all ${
                    s.featured
                      ? "border-black hover:gap-3"
                      : "border-[#eab308] text-[#eab308] hover:gap-3"
                  }`}
                >
                  Apply For This Track
                  <ArrowUpRight size={16} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
